import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import appConstants from "../constants/appConstants";
import Layout from "../components/Layout";
import Webcam from "react-webcam";
import Cropper from "react-easy-crop";
import { Modal, Button } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { 
    FaArrowLeft, FaUser, FaPhone, FaCalendarAlt, FaMapMarkerAlt, FaIdBadge, FaCheckCircle, 
    FaTimesCircle, FaKey, FaBook, FaClipboard, FaUserShield, FaAddressCard, FaEdit, FaCoins,
    FaCamera, FaUpload, FaCut, FaRedo
} from "react-icons/fa";

const getAuthToken = () => localStorage.getItem("authToken");

const StudentView = () => {
    const { id } = useParams();
    const iframeRef = useRef(null);
    const navigate = useNavigate();
    const [student, setStudent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showPhotoModal, setShowPhotoModal] = useState(false);

    const webcamRef = useRef(null);
  const [image, setImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [facingMode, setFacingMode] = useState("user");


    useEffect(() => {
        if (id) {
            fetchStudent(id); // Pass id explicitly
        } else {
            setError("Invalid Student ID.");
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (iframe && student) {
            const studentData = {
                ...student, // Keep all existing student data
                photo_path: student.photo 
                    ? `${appConstants.API_ROOT_URL}uploads/students/${student.photo}` 
                    : "default.png" // Fallback photo
            };
    
            setTimeout(() => {
                iframe.contentWindow.postMessage(studentData, "*");
                console.log("Sent student data to iframe:", studentData);
            }, 500); // Delay ensures iframe is ready
        }
    }, [student, showModal]);
    
    
    
    const toggleCamera = () => {
        setFacingMode((prevMode) => (prevMode === "user" ? "environment" : "user"));
    };

    const fetchStudent = async (studentId) => {
        const token = getAuthToken();
        if (!token) {
            alert("Unauthorized! Please log in.");
            navigate("/login");
            return;
        }

        try {
            const response = await axios.post(
                `${appConstants.API_ROOT_URL}get-student`,
                { id: studentId }, // Ensure ID is included in the JSON payload
                {
                    headers: { 
                        Authorization: `Bearer ${token}`, 
                        "Content-Type": "application/json" 
                    },
                }
            );

            if (response.data.status === "success" && response.data.data) {
                setStudent(response.data.data);
            } else {
                setError("Student not found.");
            }
        } catch (err) {
            setError("Failed to fetch student details.");
        }
        setLoading(false);
    };

    const handleAdmit = async () => {
        if (!window.confirm("Are you sure you want to admit this student?")) return;
    
        try {
            const token = getAuthToken();
            const response = await axios.post(
                `${appConstants.API_ROOT_URL}admit-student`,
                { application_no: student.application_no },
                { headers: { Authorization: `Bearer ${token}` } }
            );
    
            if (response.data.status === "success") {
                alert(`Student admitted! Admission No: ${response.data.admission_no}`);
                setStudent({...student, admission_no: response.data.admission_no});
                fetchStudent(student.id);
            } else {
                alert(response.data.message);
            }
        } catch (err) {
            alert(err.response?.data?.message || "Error admitting student.");
        }
    };


    const handlePhotoClose = () => {
        setShowPhotoModal(false);
        setImage(null);
        setCroppedImage(null);
        setCroppedAreaPixels(null);
    };
    

    const capture = () => {
        if (webcamRef.current) {
            const imageSrc = webcamRef.current.getScreenshot();
            setImage(imageSrc);
        } else {
            console.error("Webcam not initialized.");
        }
    };
    

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const cropImage = async () => {
        if (!image || !croppedAreaPixels) return;

        const img = new Image();
        img.src = image;
        img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            canvas.width = croppedAreaPixels.width;
            canvas.height = croppedAreaPixels.height;

            ctx.drawImage(
                img,
                croppedAreaPixels.x,
                croppedAreaPixels.y,
                croppedAreaPixels.width,
                croppedAreaPixels.height,
                0,
                0,
                croppedAreaPixels.width,
                croppedAreaPixels.height
            );

            setCroppedImage(canvas.toDataURL("image/jpeg"));
        };
    };
    
      // Upload cropped image
      const uploadImage = async (studentId) => {
        if (!croppedImage) return;
    
        try {
            const token = getAuthToken();
            // Convert base64 image to Blob
            const blob = await fetch(croppedImage).then((res) => res.blob());
    
            // Prepare form data
            const formData = new FormData();
            formData.append("id", studentId);  // Send student ID
            formData.append("photo", blob, "student_photo.jpg");  // Use 'photo' field name
    
            // Send request to API
            const response = await axios.post(`${appConstants.API_ROOT_URL}upload-photo`, formData, {
                headers: { 
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${token}`,
                 },
            });



            // Handle success response
            if (response.data.status === "success") {
                //alert("Upload Successful!");
                fetchStudent(student.id);
                handlePhotoClose();
            } else {
                alert(`Upload Failed: ${response.data.message}`);
            }
        } catch (err) {
            console.error("Upload Error:", err);
            alert("An error occurred while uploading the photo.");
        }
    };
    



    return (
        <Layout>
            <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center">
                    <FaUser size={28} className="me-2 text-primary" />
                    <h3 className="m-0">Student Details</h3>
                </div>

                <div className="d-flex">
                    <button className="btn btn-secondary d-flex align-items-center me-2" onClick={() => navigate(-1)}>
                        <FaArrowLeft className="me-2" /> Back
                    </button>
                    
                    <button 
                            className="btn btn-primary d-flex align-items-center ms-2" 
                            onClick={() => {
                                setImage(null);  // Clear any existing photo
                                setCroppedImage(null);
                                setShowPhotoModal(true);
                            }}
                        >
                            <FaCamera className="me-2" /> Photo
                        </button>


                    <button 
                            className="btn btn-primary d-flex align-items-center ms-2" 
                            onClick={() => setShowModal(true)}
                        >
                            <FaIdBadge className="me-2" /> ID
                        </button>

                    {student && (
                        <button 
                            className="btn btn-primary d-flex align-items-center ms-2" 
                            onClick={() => navigate(`/edit-student/${student.id}`)}
                        >
                            <FaEdit className="me-2" /> Edit
                        </button>
                    )}
                    {student?.admission_no ? (
                        <button 
                            className="btn btn-primary d-flex align-items-center ms-2" 
                            onClick={() => navigate(`/fee-management/${student.id}`)}
                        >
                            <FaCoins className="me-2" /> Fees
                        </button>
                    )
                    :
                    (
                        <button 
                            className="btn btn-success d-flex align-items-center ms-2" 
                            onClick={handleAdmit}
                        >
                            Admit
                        </button>
                    )
                    }
                </div>
            </div>


                <div className="card shadow p-4">
                    {loading ? (
                        <div className="alert alert-info text-center">Loading student details...</div>
                    ) : error ? (
                        <div className="alert alert-danger text-center">{error}</div>
                    ) : !student ? (
                        <div className="alert alert-danger text-center">Student not found.</div>
                    ) : (
                        <div className="row">
                            {/* Left Column */}
                            <div className="col-md-6">
                                <p><FaIdBadge className="text-secondary me-2" /><strong>Application No:</strong> {student.application_no}</p>
                                <p><FaCalendarAlt className="text-secondary me-2" /><strong>Date of Application:</strong> {student.date_of_application}</p>
                                <p><FaIdBadge className="text-secondary me-2" /><strong>Admission No:</strong> {student.admission_no || "N/A"}</p>
                                <p><FaCalendarAlt className="text-secondary me-2" /><strong>Date of Admission:</strong> {student.date_of_admission || "N/A"}</p>
                                <p><FaUser className="text-secondary me-2" /><strong>Name:</strong> {student.name}</p>
                                <p><FaPhone className="text-secondary me-2" /><strong>Mobile:</strong> {student.mobile}</p>
                                <p><FaMapMarkerAlt className="text-secondary me-2" /><strong>Address:</strong> {student.address || "N/A"}</p>
                                <p><FaUser className="text-secondary me-2" /><strong>Gender:</strong> {student.gender}</p>
                                <p><FaCalendarAlt className="text-secondary me-2" /><strong>Date of Birth:</strong> {student.dob}</p>
                            </div>

                            {/* Right Column */}
                            <div className="col-md-6">
                                {student.photo ? (
                                    <div className="mb-3 text-center">
                                        <img 
                                            src={`${appConstants.API_ROOT_URL}uploads/students/${student.photo}`} 
                                            alt="Student" 
                                            className="img-thumbnail rounded" 
                                            style={{ maxWidth: "150px", maxHeight: "150px" }} 
                                        />
                                    </div>
                                ) : (
                                    <p className="text-muted text-center">No Photo Available</p>
                                )}

                                <p><FaUser className="text-secondary me-2" /><strong>Parents:</strong> {student.parents}</p>
                                <p><FaBook className="text-secondary me-2" /><strong>Batch:</strong> {student.batch_detail || "N/A"}</p>
                                <p><FaClipboard className="text-secondary me-2" /><strong>Remark:</strong> {student.remark || "N/A"}</p>
                                <p><FaKey className="text-secondary me-2" /><strong>Parent Access Code:</strong> {student.parent_access_code}</p>
                                <p><FaAddressCard className="text-secondary me-2" /><strong>ID Card No:</strong> {student.id_card_no || "N/A"}</p>
                                <p>
                                    <FaCheckCircle className="text-secondary me-2" />
                                    <strong>Status: </strong> 
                                    <span className={student.status === "Active" ? "text-success fw-bold" : "text-danger fw-bold"}>
                                        {student.status}
                                    </span>
                                </p>
                            </div>

                        </div>
                    )}
                </div>
            </div>

            {showModal && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Student ID Card</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <iframe
                    ref={iframeRef}
                  src="/inst/idcard/index.html?v=1"
                  style={{ width: "100%", height: "500px", border: "none" }}
                  title="ID Card"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      )}

<Modal show={showPhotoModal} onHide={handlePhotoClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title><FaCamera className="me-2" /> Take Photo</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    {!image ? (
                        <>
                            <Webcam ref={webcamRef} screenshotFormat="image/jpeg" className="w-100" videoConstraints={{ facingMode }}  />
                            <Button variant="primary" className="w-100 mt-2" onClick={capture}>
                                <FaCamera className="me-2" /> Capture
                            </Button>
                        </>
                    ) : !croppedImage ? (
                        <>
                            <div className="crop-container" style={{ position: "relative", width: "100%", height: 300 }}>
                                <Cropper
                                    image={image}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={1} // 1:1 aspect ratio, change as needed
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onCropComplete={onCropComplete}
                                    showGrid={true} // Show grid for precise cropping
                                />
                            </div>

                            <Button variant="success" className="w-100 mt-2" onClick={cropImage}>
                                <FaCut className="me-2" /> Crop
                            </Button>
                        </>
                    ) : (
                        <>
                            <img src={croppedImage} className="w-100" alt="Cropped" />
                            <Button variant="success" className="w-100 mt-2" onClick={()=>{uploadImage(student.id)}}>
                                <FaUpload className="me-2" /> Upload
                            </Button>
                        </>
                    )}
                    <Button variant="warning" className="w-100 mt-2" onClick={() => setImage(null)}>
                        <i className="fa fa-redo"></i> Retake Photo
                    </Button>

                    <Button variant="info" className="w-100 mt-2" onClick={toggleCamera}>
                        <FaRedo className="me-2" /> Switch Camera
                    </Button>
                </Modal.Body>
            </Modal>
        </Layout>
    );
};

export default StudentView;
